import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ExternalLink, Video, ArrowLeft, Mic, Play, FileText, CheckCircle2, Loader2, Quote } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const EventCard = ({ e, past }) => (
  <div data-testid={`speaking-event-${e.engagement_id}`}
       className={`rounded-xl border p-6 transition ${past
         ? "border-border bg-card/50"
         : "border-emerald-500/40 bg-emerald-500/5 hover:border-emerald-500/70"}`}>
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="inline-flex items-center gap-1 rounded-md bg-background/60 px-2 py-1 font-medium uppercase tracking-widest">
        <Calendar className="h-3 w-3" /> {fmtDate(e.date)}
      </span>
      {e.is_virtual && (
        <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-1 font-medium text-blue-600 uppercase tracking-widest">
          <Video className="h-3 w-3" /> Virtual
        </span>
      )}
      {past && (
        <span className="rounded-md bg-muted px-2 py-1 font-medium text-muted-foreground uppercase tracking-widest">Past</span>
      )}
    </div>
    <h3 className="mt-3 font-heading text-xl font-bold">{e.title}</h3>
    {e.event_name && <div className="mt-1 text-sm text-muted-foreground">{e.event_name}</div>}
    {(e.venue || e.city) && (
      <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
        <MapPin className="h-3 w-3" /> {[e.venue, e.city].filter(Boolean).join(" · ")}
      </div>
    )}
    {e.description && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{e.description}</p>}
    <div className="mt-4 flex flex-wrap gap-3">
      {e.event_url && (
        <a href={e.event_url} target="_blank" rel="noreferrer"
           data-testid={`speaking-link-${e.engagement_id}`}
           className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
          {past ? "Event page" : "Register / details"} <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {past && e.recording_url && (
        <a href={e.recording_url} target="_blank" rel="noreferrer"
           data-testid={`speaking-recording-${e.engagement_id}`}
           className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
          <Play className="h-3 w-3" /> Watch recording
        </a>
      )}
      {past && e.slides_url && (
        <a href={e.slides_url} target="_blank" rel="noreferrer"
           data-testid={`speaking-slides-${e.engagement_id}`}
           className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 hover:underline">
          <FileText className="h-3 w-3" /> Slides
        </a>
      )}
    </div>
  </div>
);

const EMPTY_BOOKING = { org: "", contact_name: "", contact_email: "",
                         event_date: "", topic: "", audience_size: "", notes: "" };

export default function Speaking() {
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(EMPTY_BOOKING);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reel, setReel] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [reelPlaying, setReelPlaying] = useState(false);

  useEffect(() => {
    api.get("/speaking-engagements")
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/speaker-reel").then(r => setReel(r.data?.reel || null)).catch(() => {});
    api.get("/testimonials").then(r => setTestimonials(r.data?.testimonials || [])).catch(() => {});
  }, []);

  const submitBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/speaking-bookings", booking);
      setSubmitted(true);
      setBooking(EMPTY_BOOKING);
      toast.success("Thanks — Dr. Perkins's team will be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please email hello@harukihealth.com.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                data-testid="speaking-back-home">
            <ArrowLeft className="h-3 w-3" /> Haruki Health
          </Link>
          <Link to="/signin" className="text-sm text-muted-foreground hover:text-foreground">Sign in</Link>
        </div>
      </header>

      {/* Portrait/Reel + Bio hero */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 md:grid-cols-[320px_1fr] lg:gap-14 lg:py-20">
          <div className="mx-auto md:mx-0" data-testid="speaking-hero-media">
            {reel ? (
              <div className="relative overflow-hidden rounded-2xl border-4 border-emerald-500/40 shadow-xl"
                   style={{ aspectRatio: "9 / 12", width: "min(320px, 100%)" }}
                   data-testid="speaking-reel">
                {/* Uploaded MP4 — native HTML5 player */}
                {reel.kind === "upload" && (
                  <video src={mediaUrl(reel.stream_url)}
                         controls playsInline preload="metadata"
                         data-testid="speaking-reel-video"
                         className="h-full w-full object-cover"
                         poster={mediaUrl(reel.poster_url) || "/img/drjeannelle-portrait.jpg"} />
                )}
                {/* Direct URL (mp4/mov/webm hosted elsewhere) */}
                {reel.kind === "url" && reel.provider === "direct" && (
                  <video src={reel.url}
                         controls playsInline preload="metadata"
                         data-testid="speaking-reel-video"
                         className="h-full w-full object-cover"
                         poster={mediaUrl(reel.poster_url) || "/img/drjeannelle-portrait.jpg"} />
                )}
                {/* YouTube / Vimeo embed — click-to-play so it never autoplays audio */}
                {reel.kind === "url" && (reel.provider === "youtube" || reel.provider === "vimeo") && (
                  reelPlaying ? (
                    <iframe src={`${reel.embed_url}?autoplay=1`}
                             title="Speaker reel"
                             data-testid="speaking-reel-iframe"
                             allow="autoplay; encrypted-media; picture-in-picture"
                             allowFullScreen
                             className="h-full w-full" />
                  ) : (
                    <button type="button" onClick={() => setReelPlaying(true)}
                             data-testid="speaking-reel-play"
                             className="group relative h-full w-full">
                      <img src={mediaUrl(reel.poster_url) || "/img/drjeannelle-portrait.jpg"}
                           alt="Play speaker reel"
                           className="h-full w-full object-cover transition group-hover:scale-105" />
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30 transition group-hover:bg-black/40">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-2xl">
                          <Play className="h-7 w-7 translate-x-0.5 text-emerald-600" fill="currentColor" />
                        </span>
                      </span>
                      <span className="absolute bottom-3 left-3 rounded-md bg-black/60 px-2 py-1 text-[10px] font-medium uppercase tracking-widest text-white">
                        <Play className="mr-1 inline h-2.5 w-2.5" fill="currentColor" /> Speaker reel
                      </span>
                    </button>
                  )
                )}
              </div>
            ) : (
              <img src="/img/drjeannelle-portrait.jpg"
                   alt="Dr. Jeannelle Perkins, LMFT-S, EMDR"
                   data-testid="speaking-portrait"
                   className="h-64 w-64 rounded-full border-4 border-emerald-500/40 object-cover shadow-xl md:h-72 md:w-72" />
            )}
          </div>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3 py-1 text-xs font-medium text-emerald-600">
              <Mic className="h-3 w-3" /> Available for speaking
            </div>
            <h1 className="mt-3 font-heading text-4xl font-bold sm:text-5xl">
              Dr. Jeannelle Perkins
            </h1>
            <p className="mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
              LMFT-S, EMDR
            </p>
            <p className="mt-2 text-sm font-medium uppercase tracking-widest text-muted-foreground">
              Founder &amp; Clinical Director · Haruki Health
            </p>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-muted-foreground">
              <p>
                Dr. Jeannelle Perkins, LMFT-S, EMDR, is a licensed marriage and family therapist and the
                founder of Haruki Health — a clinically-governed resilience platform for first
                responders. Her work sits at the intersection of systemic therapy, moral injury
                recovery, and organizational mental fitness for high-stakes professions.
              </p>
              <p>
                She partners with police departments, fire services, EMS agencies, hospitals, and
                faith communities to build sustainable resilience practices that respect the
                confidentiality boundary responders need to actually engage. Her keynote and workshop
                topics range from post-critical-incident debriefing to systemic frameworks for
                helping the helpers.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-2 text-xs">
              {["Systemic Therapy", "First-Responder Resilience", "Moral Injury", "Faith &amp; Trauma", "Organizational Mental Fitness"].map(t =>
                <span key={t} className="rounded-full border border-border bg-muted/40 px-3 py-1 text-muted-foreground">{t}</span>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#book" data-testid="speaking-cta-book"
                 className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600">
                Book Dr. Perkins
              </a>
              <a href="mailto:hello@harukihealth.com"
                 className="inline-flex items-center gap-1 rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted/40">
                hello@harukihealth.com
              </a>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-6xl px-6 py-12 lg:py-16">
        <section>
          <div className="mb-6 flex items-baseline justify-between">
            <h2 className="font-heading text-2xl font-bold">Upcoming</h2>
            <div className="text-xs text-muted-foreground">{data.upcoming.length} scheduled</div>
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : data.upcoming.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Nothing on the calendar right now — reach out below to book her.
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2" data-testid="speaking-upcoming-grid">
              {data.upcoming.map(e => <EventCard key={e.engagement_id} e={e} />)}
            </div>
          )}
        </section>

        {data.past.length > 0 && (
          <section className="mt-16">
            <h2 className="mb-6 font-heading text-2xl font-bold">Past appearances</h2>
            <div className="grid gap-5 md:grid-cols-2" data-testid="speaking-past-grid">
              {data.past.map(e => <EventCard key={e.engagement_id} e={e} past />)}
            </div>
          </section>
        )}

        {testimonials.length > 0 && (
          <section className="mt-20" data-testid="speaking-testimonials">
            <div className="mb-2 flex items-baseline gap-2">
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                What hosts say
              </span>
            </div>
            <h2 className="font-heading text-2xl font-bold">Rave reviews from past hosts</h2>
            <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map(t => (
                <figure key={t.testimonial_id}
                        data-testid={`testimonial-${t.testimonial_id}`}
                        className="relative flex h-full flex-col rounded-xl border border-border bg-card p-6 shadow-sm transition hover:border-emerald-500/40 hover:shadow-md">
                  <Quote className="absolute -top-3 left-5 h-6 w-6 rounded-md bg-background p-1 text-emerald-500" />
                  <blockquote className="text-sm leading-relaxed text-foreground">
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-border pt-4">
                    {t.logo_url ? (
                      <img src={mediaUrl(t.logo_url)}
                           alt={t.org_name || t.author_name}
                           data-testid={`testimonial-logo-${t.testimonial_id}`}
                           className="h-10 w-10 shrink-0 rounded-md border border-border bg-white object-contain p-1" />
                    ) : t.org_name ? (
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50 text-xs font-bold text-muted-foreground">
                        {t.org_name.split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase()}
                      </span>
                    ) : null}
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{t.author_name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[t.author_role, t.org_name].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Book her form */}
        <section id="book" className="mt-20 rounded-2xl border border-border bg-card p-8 lg:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_1.4fr]">
            <div>
              <h2 className="font-heading text-3xl font-bold">Book her for your event</h2>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Tell us a little about your organization and event, and Dr. Perkins&apos;s
                team will reach out within two business days with fee ranges, availability, and
                suggested topic angles.
              </p>
              <div className="mt-6 text-xs text-muted-foreground">
                Prefer email? <a href="mailto:hello@harukihealth.com" className="text-foreground underline">hello@harukihealth.com</a>
              </div>
            </div>
            {submitted ? (
              <div data-testid="speaking-booking-thanks"
                   className="flex flex-col items-start justify-center rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-8">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <h3 className="mt-4 font-heading text-xl font-bold">Request received</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Thanks for reaching out. Dr. Perkins&apos;s team will follow up shortly at the email you provided.
                </p>
                <button onClick={() => setSubmitted(false)}
                        data-testid="speaking-booking-again"
                        className="mt-4 text-sm text-emerald-600 hover:underline">
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submitBooking} data-testid="speaking-booking-form"
                    className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="org">Organization *</Label>
                  <Input id="org" required value={booking.org} data-testid="booking-org"
                         placeholder="Boston Fire Department"
                         onChange={(e) => setBooking({ ...booking, org: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="name">Your name *</Label>
                  <Input id="name" required value={booking.contact_name} data-testid="booking-name"
                         onChange={(e) => setBooking({ ...booking, contact_name: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="email">Your email *</Label>
                  <Input id="email" type="email" required value={booking.contact_email} data-testid="booking-email"
                         onChange={(e) => setBooking({ ...booking, contact_email: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="ev-date">Event date (approx.)</Label>
                  <Input id="ev-date" type="date" value={booking.event_date} data-testid="booking-date"
                         onChange={(e) => setBooking({ ...booking, event_date: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="aud">Audience size</Label>
                  <Input id="aud" value={booking.audience_size} data-testid="booking-audience"
                         placeholder="e.g. 80 command staff" 
                         onChange={(e) => setBooking({ ...booking, audience_size: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="topic">Topic angle</Label>
                  <Input id="topic" value={booking.topic} data-testid="booking-topic"
                         placeholder="Post-incident debrief for engine companies"
                         onChange={(e) => setBooking({ ...booking, topic: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="notes">Anything else?</Label>
                  <textarea id="notes" rows={3} value={booking.notes} data-testid="booking-notes"
                            onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                </div>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={submitting} data-testid="booking-submit"
                          className="w-full sm:w-auto">
                    {submitting ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Mic className="mr-2 h-3 w-3" />}
                    Send booking request
                  </Button>
                </div>
              </form>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
